<template>
  <base-drawer
    class="cart-drawer"
    :show="show"
    :title="title"
    width="480px"
    @close="onClose"
  >
    <template v-if="cart">
      <drawer-section title="Customer" icon="icon-single-02">
        <div class="drawer-detail-row text-white">
          <span>Name</span>
          <span v-if="cart.customer_first_name || cart.customer_last_name">{{ cart.customer_first_name }} {{ cart.customer_last_name }}</span>
          <span v-else class="cart-drawer__muted font-italic">Anonymous customer</span>
        </div>
        <div v-if="cart.customer_email" class="drawer-detail-row text-white">
          <span>Email</span>
          <span class="cart-drawer__value">{{ cart.customer_email }} <i class="tim-icons el-icon-copy-document cart-drawer__copy" @click="copy(cart.customer_email)"></i></span>
        </div>
        <div v-if="cart.customer_phone" class="drawer-detail-row text-white">
          <span>Phone</span>
          <span class="cart-drawer__value">{{ formatPhone(cart.customer_phone) }} <i class="tim-icons el-icon-copy-document cart-drawer__copy" @click="copy(cart.customer_phone)"></i></span>
        </div>
        <div v-if="cart.customer_ip" class="drawer-detail-row text-white">
          <span>IP</span>
          <span>{{ cart.customer_ip }}</span>
        </div>
        <div v-if="cart.utm_source" class="drawer-detail-row text-white">
          <span>Source</span>
          <utm-attribution icon="icon-world" :source="cart.utm_source" :medium="cart.utm_medium" :campaign="cart.utm_campaign" :referrer="cart.utm_referrer" />
        </div>
      </drawer-section>

      <drawer-section title="Activity" icon="icon-time-alarm">
        <div v-if="cart.event_name" class="drawer-detail-row text-white">
          <span>Step</span>
          <span>{{ cart.event_name }}</span>
        </div>
        <div class="drawer-detail-row text-white">
          <span>First visited</span>
          <span>{{ formatDateTime(cart.timestamp) }}</span>
        </div>
        <div v-if="cart.last_updated && !String(cart.last_updated).includes('0000')" class="drawer-detail-row text-white">
          <span>Latest activity</span>
          <span>{{ formatDateTime(cart.last_updated) }}</span>
        </div>
        <div v-if="showEmailStatus" class="drawer-detail-row text-white">
          <span>Abandoned cart email</span>
          <span v-if="cart.email_sent" class="badge-tag badge-tag--primary">Sent</span>
          <span v-else class="badge-tag badge-tag--danger">Not sent</span>
        </div>
        <div v-if="cart.customer_email && tab !== 1" class="neutral-action-row neutral-action-row--attached">
          <div class="neutral-action-info">
            <span class="neutral-action-label text-white">Follow up with the customer</span>
            <span class="neutral-action-hint">{{ cart.email_sent ? 'Send the abandoned cart email again' : 'Send the abandoned cart email to this customer' }}</span>
          </div>
          <base-button size="sm" simple class="neutral-action-btn" @click="$emit('send-email', cart)">
            <i class="tim-icons icon-email-85 mr-1" style="font-size: 11px;"></i> Send email
          </base-button>
        </div>
      </drawer-section>

      <drawer-section title="Cart" icon="icon-cart">
        <div class="receipt">
          <div class="receipt-items">
            <div v-for="(line, index) in cartLines" :key="'L' + index" class="receipt-item">
              <div class="receipt-item__desc">
                <div class="receipt-item__name-row">
                  <span class="text-white">{{ line.name }}</span>
                  <span class="receipt-item__qty text-muted">x{{ line.qty }}</span>
                </div>
                <div v-if="line.meta" class="receipt-item__meta">{{ line.meta }}</div>
              </div>
              <span v-if="line.amount != null" class="text-white">{{ formatAmount(line.amount) }}</span>
            </div>
            <div v-if="cartLines.length === 0" class="receipt-empty">No items</div>
          </div>
          <div class="receipt-divider"></div>
          <div class="receipt-totals">
            <div class="receipt-row receipt-row--bold">
              <span class="text-white">Total</span>
              <span class="text-white">{{ formatAmount(cart.cart_value) }}</span>
            </div>
          </div>
        </div>
      </drawer-section>

      <drawer-section v-if="errors.length > 0" :title="errors.length === 1 ? 'Error' : 'Errors'" icon="icon-alert-circle-exc">
        <div v-for="(error, index) in errors" :key="'E' + index" class="cart-drawer__error">
          <div class="text-white">{{ error.error_message }}</div>
          <div class="cart-drawer__muted">
            {{ capitalize(error.error_type) }} · {{ formatDateTime(error.timestamp) }}<span v-if="error.error_code"> · {{ error.error_code }}</span><span v-if="promoCodeOf(error)"> · {{ promoCodeOf(error) }}</span><span v-if="error.resolved"> · Resolved</span>
          </div>
        </div>
      </drawer-section>

      <div v-if="canDelete" class="danger-action-card">
        <div class="danger-action-info">
          <span class="danger-action-label text-white">Delete cart</span>
          <span class="danger-action-hint">Removes the cart and its items permanently. Bookings and payments are not affected.</span>
        </div>
        <base-button size="sm" class="danger-action-btn" @click="$emit('delete', cart)">
          <i class="tim-icons icon-trash-simple mr-1" style="font-size: 11px;"></i> Delete
        </base-button>
      </div>
    </template>
  </base-drawer>
</template>

<script setup lang="ts">
// Read-only detail for one cart row from the carts list. The parent passes the row
// as loaded by get-active-carts (bookings_in_cart etc. already parsed) plus the
// group-aware formatters, so nothing here re-fetches or re-formats.
import { computed, watch, onBeforeUnmount, getCurrentInstance } from 'vue'
import { formatPhoneDisplay } from '@/util/phoneFormatter'

const props = defineProps({
  show: { type: Boolean, default: false },
  cart: { type: Object as () => any, default: null },
  /** 0 abandoned, 1 active, 2 errors: drives which actions and rows show. */
  tab: { type: Number, default: 0 },
  formatAmount: { type: Function as unknown as () => (v: any) => string, required: true },
  formatDate: { type: Function as unknown as () => (d: string) => string, required: true },
  formatTime: { type: Function as unknown as () => (t: string) => string, required: true },
  formatDateTime: { type: Function as unknown as () => (d: string) => string, required: true },
})
const emit = defineEmits(['update:show', 'close', 'delete', 'send-email'])
const { proxy } = getCurrentInstance() as any

const title = computed(() => {
  const c = props.cart
  if (!c) return 'Cart'
  const name = [c.customer_first_name, c.customer_last_name].filter(Boolean).join(' ').trim()
  if (name) return name
  if (c.customer_email) return c.customer_email
  return 'Anonymous customer'
})
const showEmailStatus = computed(() => props.tab === 0)
const canDelete = computed(() => props.tab === 0 && !!props.cart)
type CartLine = { name: string; meta: string; qty: number; amount: number | null }
// Bookings carry no stored price on the cart, so their amount is blank; items and
// gift cards use the value stored on their link rows.
const cartLines = computed<CartLine[]>(() => {
  const c = props.cart
  if (!c) return []
  const num = (v: any) => (v == null || v === '' || Number.isNaN(Number(v))) ? null : Number(v)
  const bookings = (c.bookings_in_cart || []).map((b: any) => ({ name: b.game_name, meta: `${props.formatDate(b.booking_date)} · ${props.formatTime(b.start_time)}`, qty: Number(b.quantity) || 1, amount: num(b.value) }))
  const items = (c.items_in_cart || []).map((i: any) => ({ name: i.name, meta: '', qty: Number(i.quantity) || 1, amount: num(i.value) }))
  const gifts = (c.gift_cards_in_cart || []).map((g: any) => ({ name: g.name, meta: '', qty: Number(g.quantity) || 1, amount: num(g.value) == null ? null : (num(g.value) as number) * (Number(g.quantity) || 1) }))
  return [...bookings, ...items, ...gifts]
})
const errors = computed(() => {
  if (!props.cart) return []
  if (Array.isArray(props.cart.errors)) return props.cart.errors
  // An errors-tab row is itself one error joined to its cart.
  if (props.cart.error_type) return [props.cart]
  return []
})

function groupCountry(): string {
  try { return JSON.parse(localStorage.getItem('group') || '{}').country || 'US' } catch (e) { return 'US' }
}
function formatPhone(phone: string) {
  return formatPhoneDisplay(phone, props.cart?.country || groupCountry())
}
function capitalize(s: string) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ') : ''
}
function promoCodeOf(error: any): string {
  try {
    const data = typeof error.error_data === 'string' ? JSON.parse(error.error_data) : error.error_data
    return data?.promo_code || data?.promoCode || ''
  } catch (e) { return '' }
}
async function copy(text: string) {
  try {
    await navigator.clipboard.writeText(text)
    proxy.$notify({ type: 'success', message: 'Copied', timeout: 1500 })
  } catch (e) {
    proxy.$notify({ type: 'warning', message: 'Could not copy', timeout: 2000 })
  }
}

function onClose() {
  emit('update:show', false)
  emit('close')
}
function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape') onClose()
}
watch(() => props.show, (visible) => {
  if (visible) document.addEventListener('keydown', onEscKey)
  else document.removeEventListener('keydown', onEscKey)
}, { immediate: true })
onBeforeUnmount(() => document.removeEventListener('keydown', onEscKey))
</script>

<script lang="ts">
export default { name: 'CartDrawer' }
</script>

<style lang="scss" scoped>
.cart-drawer__muted { color: rgba(255, 255, 255, 0.6); font-size: 12px; }
.cart-drawer__value { display: inline-flex; align-items: center; gap: 6px; min-width: 0; }

.cart-drawer__copy { font-size: 12px; opacity: 0.45; cursor: pointer; &:hover { opacity: 1; color: #e14eca; } }
.cart-drawer__error { border-left: 2px solid #fd5d93; padding: 2px 0 2px 10px; }
.cart-drawer__error + .cart-drawer__error { margin-top: 10px; }
.white-content .cart-drawer__muted { color: rgba(29, 37, 59, 0.6); }
</style>
